import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { mcpCall } from '../lib/mcp';
import { useMcpTools } from '../lib/useMcpTools';
import { pct } from '../lib/format';
import { zipToState } from '../lib/zipState';
import './Dealers.css';

// Shape returned by getDealer (row directly, or null) — mirrors the getDealer column list in
// cabana-mcp (src/tools/dealer.ts).
interface Dealer {
  id: number;
  company_name: string;
  license_number: string | null;
  website: string | null;
  primary_zip: string | null;
  program_tier: string;
  commission_rate: number | string | null; // pg numeric arrives as a string
  net_terms_days: number;
  status: string;
}

/** Normalize a bare domain ("acme.com") to an absolute URL so the link isn't treated as relative. */
const href = (url: string) => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

export function DealerDetail() {
  const { id } = useParams();
  const { can } = useMcpTools();
  const canGet = can('getDealer');

  // getDealer returns the row directly (or null) — not wrapped.
  const { data, isLoading, error } = useQuery({
    queryKey: ['mcp', 'getDealer', id],
    queryFn: () => mcpCall<Dealer | null>('getDealer', { id: Number(id) }),
    enabled: canGet && !!id,
  });
  const dealer = data ?? null;
  const state = dealer ? zipToState(dealer.primary_zip) : null;

  return (
    <section className="records">
      <header className="records__head">
        <div className="records__head-text">
          <Link to="/dealers" className="dealer-detail__back">← Dealers</Link>
          <h1 className="records__title">{dealer?.company_name ?? `Dealer #${id}`}</h1>
        </div>
      </header>

      <div className="records__panel dealer-detail__panel">
        {!canGet && <p className="dealer-detail__note">Dealer detail isn’t available for your role.</p>}
        {canGet && isLoading && <p className="dealer-detail__note">Loading…</p>}
        {canGet && error && <p className="dealer-detail__note">Couldn’t load this dealer.</p>}
        {canGet && !isLoading && !error && !dealer && (
          <p className="dealer-detail__note">No record found for this dealer.</p>
        )}
        {dealer && (
          <dl className="dealer-detail">
            <div className="dealer-detail__row">
              <dt>Status</dt>
              <dd>
                <span className={`records__tag records__tag--${dealer.status === 'active' ? 'ok' : 'warn'}`}>
                  {dealer.status || '—'}
                </span>
              </dd>
            </div>
            <div className="dealer-detail__row"><dt>Program tier</dt><dd>{dealer.program_tier || '—'}</dd></div>
            <div className="dealer-detail__row"><dt>Commission</dt><dd>{pct(dealer.commission_rate)}</dd></div>
            <div className="dealer-detail__row">
              <dt>Net terms</dt>
              <dd>{dealer.net_terms_days != null ? `${dealer.net_terms_days} days` : '—'}</dd>
            </div>
            <div className="dealer-detail__row"><dt>State</dt><dd>{state ?? '—'}</dd></div>
            <div className="dealer-detail__row"><dt>ZIP</dt><dd>{dealer.primary_zip || '—'}</dd></div>
            <div className="dealer-detail__row"><dt>License #</dt><dd>{dealer.license_number || '—'}</dd></div>
            <div className="dealer-detail__row">
              <dt>Website</dt>
              <dd>
                {dealer.website ? (
                  <a className="records__link" href={href(dealer.website)} target="_blank" rel="noreferrer">
                    {dealer.website}
                  </a>
                ) : (
                  '—'
                )}
              </dd>
            </div>
            <div className="dealer-detail__row">
            </div>
            <div className="dealer-detail__row"><dt>Dealer ID</dt><dd>{dealer.id}</dd></div>
          </dl>
        )}
      </div>
    </section>
  );
}
